// 設備管理（plan/vms-web-v0.md §5.3）：設定表單（DEV-06）、配對（DEV-02）、解綁（DEV-13）、健康（DEV-10）；影像牆版面（LIVE-01 vms.layouts）。
import { KinioError, kinioFetch, planeBase, idempotencyKey } from '@kinio/web-sdk';
import { kinioLabel } from './kinioLabel';

const homeUrl = (api: string, homeId: string, path: string) => `${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}${path}`;

// ---- 設定表單（GET /v1/homes/{id}/vms/devices/{device}/settings）----

export type SettingControl = 'switch' | 'select' | 'slider' | 'stepper' | 'text' | 'readout' | 'button' | 'custom';

/** 設定分層（SPEC #platform-ui-tiers）：app-api 已依這台設備求值（tier_when 已套），前端只信它、不自己算述詞。 */
export type SettingTier = 'common' | 'advanced' | 'technical';

/** int 的固定檔位（依值排序）；有 presets 的 int 畫成選單。 */
export interface SettingPreset {
  value: number;
  label: string;
  label_id?: string;
}

/** 依能力換層的規則（投影原樣帶來；前端不求值，只信 tier）。 */
export interface SettingTierRule {
  if: 'battery' | 'channel' | 'gateway' | 'mycam';
  tier: SettingTier;
}

/** 分段控制的宣告（頂層 segments）。 */
export interface SettingSegment {
  label: string;
  label_id?: string;
  none_label: string;
  none_label_id?: string;
}

export type SettingEditor = 'zones' | 'zone_grid' | 'privacy_masks' | 'schedule';

export interface SettingItem {
  key: string;
  label: string;
  /** 穩定 label id（翻譯表的 key；= key） */
  label_id?: string;
  control: SettingControl;
  type?: string;
  enum?: Record<string, string>;
  enum_labels?: Record<string, string>;
  /** 值 → 選項文案的穩定 id（`<key>.enum.<名稱>`） */
  enum_label_ids?: Record<string, string>;
  min?: number;
  max?: number;
  units?: string;
  write_authz?: string;
  per_channel?: boolean;
  depends_on?: string;
  /** 配 depends_on：上層是 enum 時，值在清單內本項才啟用 */
  depends_value?: number[];
  confirm?: string;
  /** 可寫 bool 關掉時的確認文案（ui.yaml confirm_off；沒有 = 開關都用 confirm） */
  confirm_off?: string;
  destructive?: boolean;
  help?: string;
  /** 求值後的層；hidden 的條目不會出現在回應裡 */
  tier?: SettingTier;
  tier_when?: SettingTierRule[];
  pin?: ('status' | 'quick')[];
  /** 由專屬頁畫（VMS 照樣列在所屬 section；poe 例外，只在「現場設備」分頁畫） */
  page?: 'recording_cloud' | 'recording_sd' | 'storage' | 'firmware' | 'local_access' | 'zones' | 'poe';
  /** raw 屬性用哪個共用編輯器（ui.yaml editor，SPEC #ls2505-334）；沒有 = 只顯示「請用設備 App」 */
  editor?: SettingEditor;
  presets?: SettingPreset[];
  /** 分段控制名稱（頂層 segments） */
  segment?: string;
  /** 搜尋同義詞（本輪只解析，搜尋列第二階段） */
  keywords?: string[];
  value: unknown;
  desired?: unknown;
  pending: boolean;
  can_write: boolean;
}

export interface SettingCluster {
  cluster: string;
  label: string;
  label_id?: string;
  items: SettingItem[];
}

export interface SettingSection {
  name: string;
  label: string;
  label_id?: string;
  icon?: string;
  clusters: SettingCluster[];
}

export interface DeviceSettings {
  sections: SettingSection[];
  /** 分段控制名稱 → 宣告 */
  segments?: Record<string, SettingSegment>;
  shadow_version: number;
  updated_at: string;
}

/** 會影響既有錄影與觀看的設定（標準整合 §7.2）：碼流 / 畫質 / 解析度 / 編碼。 */
export function affectsStreams(key: string): boolean {
  const [cluster, attr = ''] = key.split('.');
  return cluster === 'kinio_streaming' || /quality|resolution|codec|bitrate|fps|stream/.test(attr);
}

/** 寫入這個值要顯示的確認文案：bool 寫 false 且模型有 confirm_off 就用它，否則 confirm（SPEC #kinio-station-control）。 */
export function confirmText(it: SettingItem, value: unknown): string | undefined {
  // confirm 照語系翻（`<key>.confirm`，B-22）；confirm_off 翻譯表還沒有，用投影原文
  return value === false && it.confirm_off ? it.confirm_off : it.confirm ? kinioLabel(`${it.key}.confirm`, it.confirm) : undefined;
}

export const deviceApi = {
  /** 這台（通道各自算）最新的事件快照（SPEC #events-snapshot；沒有 404 NO_SNAPSHOT）：編輯器底圖先用它，按〔看即時〕才開即時影像。 */
  latestSnapshot: (api: string, homeId: string, device: string) =>
    kinioFetch<{ snapshot_id: string; event_id?: string; captured_at?: string; url: string; url_expires_at?: string }>(
      homeUrl(api, homeId, `/vms/devices/${encodeURIComponent(device)}/snapshots/latest`),
    ),
  settings: (api: string, homeId: string, device: string) =>
    kinioFetch<DeviceSettings>(homeUrl(api, homeId, `/vms/devices/${encodeURIComponent(device)}/settings`)),
  unbind: (api: string, homeId: string, device: string) =>
    kinioFetch<{ unbound: boolean; device_reset: boolean }>(homeUrl(api, homeId, `/things/${encodeURIComponent(device)}`), { method: 'DELETE' }),
  /** 設備改名（PATCH /v1/homes/{home}/things/{device} {display_name}；DEV-02 配對完取名同一支，名稱 1–32 字，不合 422 INVALID_NAME）。 */
  rename: (api: string, homeId: string, device: string, displayName: string) =>
    kinioFetch(homeUrl(api, homeId, `/things/${encodeURIComponent(device)}`), { method: 'PATCH', body: { display_name: displayName } }),
  /** Kinio Node 遠端 console 的一次性票（SPEC #node-ttl-relay）：有 device.read 就能拿 read / write；60 秒內連 ws_url、只能用一次。
   *  沒權限 404、非 Kinio Node 422 NOT_SUPPORTED、離線 409 DEVICE_OFFLINE、每人每台每分鐘 30 張 429 RATE_LIMITED。 */
  consoleTicket: (api: string, homeId: string, device: string, mode: 'read' | 'write') =>
    // can_write：這個人能不能拿 write 票（Eddy 2026-10-14 選單：寫入只限管理角色，SPEC #node-ttl-console-writer）；舊平台沒這欄 = 照舊能寫
    kinioFetch<{ ticket: string; ws_url: string; expires_at: string; mode: 'read' | 'write'; can_write?: boolean }>(
      homeUrl(api, homeId, `/things/${encodeURIComponent(device)}/console`),
      { method: 'POST', body: { mode } },
    ),
  /** 區網直連 token（SPEC #node-ttl-direct）：只在使用者明確要自己連線時拿（#node-ttl-auto-route）；token 只出現這一次。 */
  ttlDirectToken: (api: string, homeId: string, device: string) =>
    kinioFetch<{ token_id: string; token: string; expires_at: string; ttl_seconds: number; aid: string; lan: { ip: string; port: number } | null }>(
      homeUrl(api, homeId, `/things/${encodeURIComponent(device)}/ttl-direct-token`),
      { method: 'POST', body: {} },
    ),
};

// ---- 配對（provisioning §5：POST /v1/pairing/sessions）----

export type PairingStatus = 'pending' | 'connected' | 'bound' | 'failed' | 'canceled' | 'expired';

/** 配對 session（id 前綴 prs-，欄名 binding_id 照平台）；device_ref = 設備本身的 device_id。 */
export interface PairingSession {
  binding_id: string;
  home_id: string;
  device_ref: string;
  method: string;
  status: PairingStatus;
  pairing_code?: string;
  pair_url?: string;
  device_id?: string;
  fail_reason?: string | null;
  expires_at: string;
  bound_at?: string;
}

export const pairingApi = {
  start: (api: string, body: { home_id: string; device_ref: string; method: 'lan' | 'qr' | 'ble' | 'ap' }) =>
    kinioFetch<PairingSession>(`${planeBase(api)}/v1/pairing/sessions`, { method: 'POST', body }),
  get: (api: string, id: string) => kinioFetch<PairingSession>(`${planeBase(api)}/v1/pairing/sessions/${encodeURIComponent(id)}`),
  cancel: (api: string, id: string) => kinioFetch(`${planeBase(api)}/v1/pairing/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

/** device_id 形狀（component/aid：TYPE-XXXXXXXXXXXXXXXX-C，16 碼 Crockford base32 + 檢查碼）；只做前端粗檢，伺服器再驗。 */
export function looksLikeAid(s: string): boolean {
  return /^[A-Z0-9]{3}-[0-9A-HJKMNP-TV-Z]{16}-[0-9A-HJKMNP-TV-Z*~$=U]$/.test(s.trim().toUpperCase());
}

// ---- 儲存健康（kinio_storage reported，DEV-10 / HEALTH-01）----

/**
 * kinio_storage.disks[]（protocol clusters.yaml 0xFC91，component/protocol 96af893）：
 *   新形狀（Kinio Station NT98323 起）= **每顆實體碟一筆**：rotational、record（錄影卷在這顆）、total_bytes = 實體容量、
 *     free_bytes = 碟上 Kinio 池可用合計、pools = 這顆碟上的 Kinio 分割（原廠保留分割不列）。
 *   舊形狀（emulator、舊 nvrd、Mac / Windows Station、MyCam）= 一筆錄影卷，沒有 rotational / pools，total_bytes = 錄影卷容量。
 */
export interface DiskInfo {
  no?: number;
  device?: string;
  model?: string;
  serial?: string;
  state?: string;
  smart?: string;
  /** true = HDD（旋轉）、false = SSD；舊形狀沒有 */
  rotational?: boolean;
  /** 碟的種類（SPEC #storage-124 `disks[].kind` = svol_t.kind）：sd / hdd / ssd / usb / flash（內建 flash 系統卷，只回報用量）；有帶就照它，沒帶用 rotational */
  kind?: string;
  /** 錄影卷在這顆碟上；舊形狀沒有 */
  record?: boolean;
  total_bytes?: number;
  free_bytes?: number;
  rec_bytes?: number;
  free_hours?: number;
  pools?: PoolInfo[];
  totalBytes?: number;
  freeBytes?: number;
  /** S.M.A.R.T. 摘要（SPEC #storage-smart；讀不到的欄位設備不送）：溫度 °C、通電小時、重新配置 / 待處理 / 無法修正扇區數 */
  smart_temp_c?: number;
  smart_power_on_hours?: number;
  smart_realloc?: number;
  smart_pending?: number;
  smart_uncorrectable?: number;
  /** 這次開機下過的自我測試；沒下過不帶 */
  smart_test?: { state?: string; result?: string; type?: string; at?: number; auto?: boolean }; // auto = 每週自動排的（SPEC #storage-smart）
}

/** disks[].pools[]：Kinio 的分割（roles record | system | index；in_use = nvrd 目前真的在用，index 池在索引搬遷前是 false）。 */
export interface PoolInfo {
  roles?: string[];
  device?: string;
  label?: string;
  mount?: string;
  in_use?: boolean;
  total_bytes?: number;
  free_bytes?: number;
}

/** 畫面用的池列：帶上所在碟。 */
export interface PoolRow extends PoolInfo {
  diskNo?: number;
  diskModel?: string;
  diskRotational?: boolean;
  diskKind?: DiskKind;
}

/** 畫面上的碟種類 */
export type DiskKind = 'sd' | 'hdd' | 'ssd' | 'usb' | 'flash';
const DISK_KINDS: readonly string[] = ['sd', 'hdd', 'ssd', 'usb', 'flash'];

/** 碟種類：有 `kind`（認得的值）照 `kind`；沒帶的舊形狀用 rotational（true = HDD、false = SSD）；都沒有 → undefined（app/vms#3） */
export function diskKind(d: Pick<DiskInfo, 'kind' | 'rotational'>): DiskKind | undefined {
  const k = typeof d.kind === 'string' ? d.kind.trim().toLowerCase() : '';
  if (DISK_KINDS.includes(k)) return k as DiskKind;
  return d.rotational === true ? 'hdd' : d.rotational === false ? 'ssd' : undefined;
}

export interface StorageHealth {
  /** 只有記憶卡（攝影機只報 sd_present / sd_status / sd_capacity，沒有 disks） */
  sdCard?: boolean;
  /**
   * 記憶卡滿了在循環覆寫：sd_status 1（正常；卡滿但覆寫照常也是 1）而且可用不到總容量 3%。容量改寫「共 Y · 循環錄影中」＋
   * 「卡滿後會自動蓋掉最舊的錄影，不會停止錄影」，不寫「已用 Y / 共 Y」（lsraw 卡寫滿一圈後一直報滿；SPEC App SD 卡頁同一條，plan e8bdbda）。
   */
  sdLooping?: boolean;
  /** 錄影可用容量（頂層 total_bytes = 錄影卷合計；沒報時退回 record 池合計）——保存天數用它，不是實體容量 */
  totalBytes?: number;
  freeBytes?: number;
  recordKbps?: number;
  overwrite?: number;
  /** kinio_storage.overwrite 的語意（protocol clusters.yaml：-1 auto | 0 off | N 保留天數）；沒回報 = undefined */
  overwriteMode?: OverwriteMode;
  disks: DiskInfo[];
  /** 新形狀（disks 是實體碟：有 pools 或 rotational）；false = 舊形狀，disks 一筆 = 錄影卷 */
  physical: boolean;
  /** 所有碟上的 Kinio 池（新形狀才有） */
  pools: PoolRow[];
  /** 預估可保存天數（自動覆寫：錄影容量 / 每日錄影量；保留 N 天：取 N 與前者較小；不覆寫：錄影可用 / 每日錄影量）。沒有錄影位元率就 undefined。 */
  retentionDays?: number;
  /** 最近 24 小時（這台電腦看到的）平均錄影位元率；保存天數用它估（bitrateAvg.ts，第六輪 B6-07） */
  avgKbps?: number;
  level: 'ok' | 'warn' | 'alert' | 'unknown';
  reasons: string[];
  /** 整體 SMART（最差的那顆；kinio_storage.smart 與 disks[].smart）；沒有可判的值 = unknown（不告警） */
  smart: SmartState;
}

export type SmartState = 'ok' | 'warn' | 'fail' | 'unknown';

/** kinio_storage.overwrite：-1 = 自動覆寫（滿了蓋最舊的）、0 = 不覆寫（滿了停錄）、N > 0 = 保留 N 天。 */
export type OverwriteMode = { kind: 'auto' } | { kind: 'off' } | { kind: 'days'; days: number };

/** 照 protocol clusters.yaml 的值域解讀；-1 是正常的「自動覆寫」，不是未知。其他負數 / 非整數 / 沒回報 → undefined（不顯示、不告警）。 */
export function overwriteMode(v: unknown): OverwriteMode | undefined {
  if (typeof v !== 'number' || !Number.isInteger(v)) return undefined;
  if (v === -1) return { kind: 'auto' };
  if (v === 0) return { kind: 'off' };
  if (v > 0) return { kind: 'days', days: v };
  return undefined;
}

/**
 * kinio_storage.smart 的列舉 ok / warn / fail / unknown（Eddy 2026-09-28）。空字串、"Not Supported"、查不到 → unknown（顯示「未知」、不告警）。
 * 過渡期容錯舊韌體的字串：PASSED / Verified / OK… → ok；FAILING / FAILED… → fail。
 */
export function smartState(v: unknown): SmartState {
  if (typeof v !== 'string') return 'unknown';
  const x = v.trim().toLowerCase();
  if (!x || /^(unknown|not[ _-]?supported|n\/?a|none|-)$/.test(x)) return 'unknown';
  if (/^(ok|pass|passed|verified|good|normal|healthy)$/.test(x)) return 'ok';
  if (/^(warn|warning|degraded)$/.test(x)) return 'warn';
  return 'fail';
}

const SMART_RANK: Record<SmartState, number> = { unknown: 0, ok: 1, warn: 2, fail: 3 };

/** 池的用途：roles 有 record 就算錄影池 */
export const isRecordPool = (p: PoolInfo) => Array.isArray(p.roles) && p.roles.includes('record');

/**
 * 儲存池表一列的容量與可用（第十三輪 B13-07：摘要「已用 5.3 GB（可用 10.2 GB）」、錄影池「已用 5.2 GB（可用 10.3 GB）」——頂層與池的 free_bytes 取樣時間不同）：
 * 錄影池只有一個、而且它的 total_bytes 等於頂層 total_bytes（同一個錄影卷）時用頂層的 free_bytes，跟上方摘要同一個欄位；其他照池自己的值。
 */
export function poolUsage(s: Pick<StorageHealth, 'totalBytes' | 'freeBytes' | 'pools'>, p: PoolInfo): { total?: number; free?: number } {
  const own = { total: p.total_bytes, free: p.free_bytes };
  if (!isRecordPool(p) || s.freeBytes === undefined || s.totalBytes === undefined) return own;
  if (s.pools.filter(isRecordPool).length !== 1 || p.total_bytes !== s.totalBytes) return own;
  return { total: p.total_bytes, free: s.freeBytes };
}

/**
 * 記憶卡容量（protocol kinio_storage.sd_capacity：設備報的字串 "total/used/free"，單位 MB = 1048576 bytes；
 * 跟 App 共用規則 kinio-app-core docs/testdata/kinio-recording-rows.json）：三段都是非負整數、total > 0、used + free ≤ total 才算，
 * 其他（含沒插卡的 "0/0/0"、格式不對）回 null = 讀不到容量（第四輪 B4-03）。
 */
export function parseSdCapacity(v: unknown): { total: number; used: number; free: number } | null {
  if (typeof v !== 'string') return null;
  const m = v.trim().match(/^(\d+)\/(\d+)\/(\d+)$/);
  if (!m) return null;
  const [total, used, free] = [m[1], m[2], m[3]].map(Number);
  if (!(total > 0) || used + free > total) return null;
  const MB = 1048576;
  return { total: total * MB, used: used * MB, free: free * MB };
}

/** 記憶卡狀態（kinio_storage.sd_status）→ 儲存健康的原因：2 異常 / 3 已滿（不覆寫、停錄）/ 6 需要格式化；1 正常、4 格式化中、5 沒插卡不算異常。 */
const SD_STATUS_REASON: Record<number, ['warn' | 'alert', string]> = { 2: ['alert', 'sd_abnormal'], 3: ['warn', 'sd_no_space'], 6: ['alert', 'sd_needs_format'] };

export function storageHealth(storage: Record<string, unknown> | undefined): StorageHealth | null {
  if (!storage || typeof storage !== 'object') return null;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  const disks = (Array.isArray(storage.disks) ? storage.disks : []).filter((d): d is DiskInfo => !!d && typeof d === 'object');
  const physical = disks.some((d) => Array.isArray(d.pools) || typeof d.rotational === 'boolean' || typeof d.kind === 'string');
  const pools: PoolRow[] = disks.flatMap((d) =>
    (Array.isArray(d.pools) ? d.pools : [])
      .filter((p): p is PoolInfo => !!p && typeof p === 'object')
      .map((p) => ({ ...p, diskNo: d.no, diskModel: d.model, diskRotational: d.rotational, diskKind: diskKind(d) })),
  );
  // 錄影可用容量：頂層 total / free（錄影卷合計，protocol 96af893）；韌體沒報頂層時用 record 池加總
  const recPools = pools.filter(isRecordPool);
  const sum = (k: 'total_bytes' | 'free_bytes') =>
    recPools.length && recPools.every((p) => num(p[k]) !== undefined) ? recPools.reduce((a, p) => a + (p[k] as number), 0) : undefined;
  const out: StorageHealth = {
    totalBytes: num(storage.total_bytes) ?? sum('total_bytes'),
    freeBytes: num(storage.free_bytes) ?? sum('free_bytes'),
    recordKbps: num(storage.record_kbps),
    overwrite: num(storage.overwrite),
    overwriteMode: overwriteMode(storage.overwrite),
    disks,
    physical,
    pools,
    level: 'ok',
    reasons: [],
    smart: 'unknown',
  };
  // 攝影機的記憶卡（沒有 disks / 頂層容量，只報 sd_present / sd_status / sd_capacity）：容量用 sd_capacity，
  // 摘要、概覽、設定列同一份數字；讀不到才寫「讀不到容量」（第四輪 B4-03）
  const sdCap = storage.sd_present === false ? null : parseSdCapacity(storage.sd_capacity);
  if (sdCap) {
    if (out.totalBytes === undefined) {
      out.totalBytes = sdCap.total;
      out.freeBytes = sdCap.free;
    }
    const sd = disks.find((d) => diskKind(d) === 'sd');
    if (sd && sd.total_bytes === undefined && sd.totalBytes === undefined) Object.assign(sd, { total_bytes: sdCap.total, free_bytes: sdCap.free });
    if (num(storage.sd_status) === 1 && sdCap.free / sdCap.total < 0.03) out.sdLooping = true;
  }
  // 只有記憶卡（攝影機）：不另畫碟表，摘要一行就夠
  if (storage.sd_present === true && !disks.length) out.sdCard = true;
  const smartTop = storage.smart;
  const bump = (lv: 'warn' | 'alert', r: string) => {
    if (lv === 'alert' || out.level !== 'alert') out.level = lv;
    if (!out.reasons.includes(r)) out.reasons.push(r); // 同一個原因只列一次（多顆碟 + 整體 smart）
  };
  const smartSeen = (v: unknown) => {
    const s = smartState(v);
    if (SMART_RANK[s] > SMART_RANK[out.smart]) out.smart = s;
  };
  for (const d of disks) {
    const st = (typeof d.state === 'string' ? d.state : '').toLowerCase();
    if (st && !['normal', 'ok', 'recording', 'idle', 'sleep'].includes(st)) bump(st === 'full' ? 'warn' : 'alert', `disk_${st}`);
    smartSeen(d.smart);
  }
  smartSeen(smartTop);
  if (out.smart === 'fail') bump('alert', 'smart');
  else if (out.smart === 'warn') bump('warn', 'smart_warn');
  if (out.totalBytes === 0) bump('alert', 'no_disk');
  const sdSt = SD_STATUS_REASON[num(storage.sd_status) ?? 0];
  if (sdSt && storage.sd_present !== false) bump(sdSt[0], sdSt[1]);
  // 插著記憶卡但讀不到容量：狀態寫「未回報」，概覽寫「SD 卡 · 讀不到容量（設備沒有回報）」
  if (!sdCap && storage.sd_present === true && out.totalBytes === undefined && out.level === 'ok') out.level = 'unknown';
  const ow = out.overwriteMode;
  if (out.totalBytes && out.freeBytes !== undefined && ow?.kind === 'off' && out.freeBytes / out.totalBytes < 0.05) bump('warn', 'almost_full');
  if (out.recordKbps && out.recordKbps > 0) {
    const perDay = ((out.recordKbps * 1000) / 8) * 86400;
    const base = ow?.kind === 'off' ? out.freeBytes : out.totalBytes;
    if (base !== undefined) out.retentionDays = base / perDay;
  }
  // 保留 N 天：設備最多留 N 天，容量不夠時更短（沒有位元率一樣不估）
  if (ow?.kind === 'days' && out.retentionDays !== undefined) out.retentionDays = Math.min(ow.days, out.retentionDays);
  if (out.totalBytes === undefined && disks.length === 0) out.level = 'unknown';
  return out;
}

export function fmtBytes(n: number | undefined): string {
  if (n === undefined) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1000 && i < u.length - 1) {
    v /= 1000;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
}

// ---- 影像牆版面（/v1/vms/layouts）----

export interface LayoutCell {
  home_id?: string;
  device_ref?: string;
  stream?: 'main' | 'sub';
  denied?: boolean;
}

export interface ServerLayout {
  layout_id: string;
  scope: 'user' | 'org';
  owner_org_id?: string;
  name: string;
  grid: number;
  /** 0 / 沒有 = 預設版面；1–8 = 自訂格線（LIVE-01） */
  cols?: number;
  cells: (LayoutCell | null)[];
  /** 輪巡（LIVE-05，features/live/tour.ts 的 TourSpec）；沒有 = 不輪巡 */
  tour?: unknown;
  version: number;
  can_write: boolean;
  updated_at: string;
  /** 前端補：這份來自哪個 plane。 */
  api: string;
}

export const layoutApi = {
  list: (api: string) =>
    kinioFetch<{ data: Omit<ServerLayout, 'api'>[]; org_ids: string[]; org_admin_of: string[] }>(`${planeBase(api)}/v1/vms/layouts`).then((r) => ({
      data: (r?.data ?? []).map((l) => ({ ...l, api })),
      orgIds: r?.org_ids ?? [],
      orgAdminOf: r?.org_admin_of ?? [],
    })),
  create: (
    api: string,
    body: { scope: 'user' | 'org'; org_id?: string; name: string; grid: number; cols?: number; cells: (LayoutCell | null)[]; tour?: unknown },
  ) => kinioFetch<Omit<ServerLayout, 'api'>>(`${planeBase(api)}/v1/vms/layouts`, { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey() } }),
  update: (
    api: string,
    id: string,
    body: { expected_version: number; name: string; grid: number; cols?: number; cells: (LayoutCell | null)[]; tour?: unknown },
  ) => kinioFetch<Omit<ServerLayout, 'api'>>(`${planeBase(api)}/v1/vms/layouts/${encodeURIComponent(id)}`, { method: 'PUT', body }),
  remove: (api: string, id: string, version: number) =>
    kinioFetch(`${planeBase(api)}/v1/vms/layouts/${encodeURIComponent(id)}?expected_version=${version}`, { method: 'DELETE' }),
};

/** 對設備下命令（thingProxy POST …/commands，op set；權限由 app-api 依命令 id 驗，LIVE-07 / DEV-11）。 */
/** 互動命令（警報器、補光燈）等設備 ack 的上限：三端統一 5 秒，也是設備端的執行期限（plan SPEC #cmd-expiry，Eddy 2026-10-23）。 */
export const INTERACTIVE_WAIT_SECONDS = 5;

export function deviceCommand(api: string, homeId: string, device: string, id: string, payload?: Record<string, unknown>, waitSeconds = 8) {
  return kinioFetch<{ status?: string; result?: unknown }>(
    `${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/things/${encodeURIComponent(device)}/commands`,
    {
      method: 'POST',
      body: { op: 'set', id, payload, wait_seconds: waitSeconds },
    },
  );
}

/**
 * Station / NVR 的本地 RPC（命令代理 op:"rpc"，id = 方法名、params 包在 payload.value；ADR-004 §2.5）。
 * 設備依發起者的家戶角色限制能跑的方法（app-api 帶 role）；設備回錯誤 → KinioError（code 用設備的錯誤碼）。
 */
export async function stationRpc<T>(dev: { apiEndpoint: string; homeId: string; aid: string }, method: string, params?: Record<string, unknown>, waitSeconds = 15): Promise<T> {
  const o = await kinioFetch<{ status?: string; result?: { status?: string; payload?: { result?: T; code?: string; message?: string } } }>(
    `${planeBase(dev.apiEndpoint)}/v1/homes/${encodeURIComponent(dev.homeId)}/things/${encodeURIComponent(dev.aid)}/commands`,
    { method: 'POST', body: { op: 'rpc', id: method, ...(params ? { payload: { value: params } } : {}), wait_seconds: waitSeconds } },
  );
  const st = o?.result?.status ?? '';
  if (o?.status !== 'acked' || (st && st !== 'ok' && st !== 'accepted')) {
    const p = o?.result?.payload;
    const code = (p?.code && /[A-Za-z]/.test(p.code) ? p.code : st.toUpperCase()) || 'DEVICE_ERROR';
    throw new KinioError(502, code, p?.message || `${method} failed`);
  }
  return o.result?.payload?.result as T;
}
